import { ActionLabel } from './ui/ActionLabel';
import { type MouseEvent } from 'react';
import { Linkedin } from 'lucide-react';
import { Menu, User, X } from './Icons';
import { BrandLogo } from './BrandLogo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useTranslation } from '../contexts/TranslationContext';
import { publicSocialLinks } from '../config/publicSocialLinks';
import { localizePublicHref } from '../utils/publicRoutes';

export type PublicNavigationLink = {
  id: string;
  labelKey: string;
  href?: string;
};

type LandingHeaderProps = {
  homeHref?: string;
  isDesktopNavigationVisible?: boolean;
  isMobileMenuOpen: boolean;
  navigationLinks: PublicNavigationLink[];
  onToggleMobileMenu: () => void;
  onNavigationClick?: (event: MouseEvent<HTMLAnchorElement>, id: string) => void;
  onMobileNavigationClick?: (id: string) => void;
  onGetStarted?: () => void;
  onSignIn: () => void;
  onLoginIntent: () => void;
};

export function LandingHeader({
  homeHref = '/#hero',
  isDesktopNavigationVisible = false,
  isMobileMenuOpen,
  navigationLinks,
  onToggleMobileMenu,
  onNavigationClick,
  onMobileNavigationClick,
  onGetStarted,
  onSignIn,
  onLoginIntent,
}: LandingHeaderProps) {
  const { language, t } = useTranslation();
  const localizedHref = (href: string) => localizePublicHref(href, language);
  const mobileFeaturedLinks = navigationLinks.filter(
    ({ id }) => id === 'manifesto' || id === 'pricing',
  );

  return (
    <nav
      id="public-navigation"
      className="theme-public-header fixed inset-x-3 top-3 z-50 flex max-h-[calc(100svh-1.5rem)] flex-col overflow-visible rounded-xl border transition-[background-color,border-color,backdrop-filter] duration-300 sm:inset-x-4 sm:backdrop-blur-xl lg:block lg:max-h-none"
    >
      <div className="mx-auto flex w-full max-w-7xl shrink-0 items-center justify-between gap-1 px-2 py-2.5 sm:gap-4 sm:px-6 sm:py-4">
        <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
          <a
            href={localizedHref(homeHref)}
            onClick={onNavigationClick
              ? (event) => onNavigationClick(event, 'hero')
              : undefined}
            className="brand-focus-ring flex shrink-0 rounded-lg"
            aria-label={t('appName')}
          >
            <BrandLogo
              variant="platform"
              className="h-7 max-w-[4.25rem] min-[380px]:max-w-[5rem] sm:h-12 sm:max-w-[14rem] lg:max-w-[9rem] xl:max-w-[14rem]"
              loading="eager"
            />
          </a>
        </div>
        <div
          data-desktop-section-navigation
          aria-hidden={!isDesktopNavigationVisible}
          className={`hidden min-w-0 flex-1 items-center justify-center gap-0 transition-[opacity,transform] duration-300 ease-out motion-reduce:transform-none motion-reduce:transition-none lg:flex ${
            isDesktopNavigationVisible
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-1 opacity-0'
          }`}
        >
          {navigationLinks.map((link) => (
            <a
              key={link.id}
              href={localizedHref(link.href ?? `/#${link.id}`)}
              tabIndex={isDesktopNavigationVisible ? undefined : -1}
              onClick={link.href || !onNavigationClick
                ? undefined
                : (event) => onNavigationClick(event, link.id)}
              className="brand-nav-link brand-focus-ring inline-flex min-h-10 shrink-0 items-center rounded-lg px-1.5 text-[11px] font-bold xl:px-3 xl:text-sm"
            >
              {t(link.labelKey)}
            </a>
          ))}
        </div>
        <div className="flex min-w-0 items-center gap-1 sm:gap-3">
          <div
            className="hidden min-w-0 items-center justify-end gap-0.5 md:flex lg:hidden"
            data-mobile-featured-navigation
          >
            {mobileFeaturedLinks.map((link) => (
              <a
                key={link.id}
                href={localizedHref(link.href ?? `/#${link.id}`)}
                onClick={link.href || !onMobileNavigationClick
                  ? undefined
                  : (event) => {
                      event.preventDefault();
                      onMobileNavigationClick(link.id);
                    }}
                className="brand-nav-link brand-focus-ring inline-flex min-h-8 items-center whitespace-nowrap rounded-md px-1.5 text-[10px] font-bold leading-none min-[380px]:px-2 min-[380px]:text-xs"
              >
                {t(link.id === 'pricing' ? 'nav.pricingShort' : link.labelKey)}
              </a>
            ))}
          </div>
          {onGetStarted && (
            <button
              type="button"
              onClick={onGetStarted}
              onFocus={onLoginIntent}
              onPointerEnter={onLoginIntent}
              data-header-free-trial
              className="brand-button brand-button-primary brand-focus-ring min-h-11 shrink-0 px-2 py-1.5 text-[11px] leading-tight sm:px-3 sm:text-xs xl:px-4 xl:text-sm"
            >
              <ActionLabel text={`${t('hero.ctaLabel')}\n${t('common.noCardRequired')} · ${t('login.noPasswordTag')}`} />
            </button>
          )}
          <span className="theme-header-separator hidden h-6 w-px sm:block lg:hidden" aria-hidden="true" />
          <span className="group relative hidden sm:inline-flex lg:hidden">
            <a
              href={publicSocialLinks.linkedinProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="brand-nav-link brand-focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors sm:h-10 sm:w-10"
              aria-label={t('footer.linkedinAria')}
              aria-describedby="header-linkedin-tooltip"
            >
              <Linkedin className="h-5 w-5 brand-linkedin-icon" aria-hidden="true" />
            </a>
            <span
              id="header-linkedin-tooltip"
              role="tooltip"
              className="app-tooltip-surface pointer-events-none absolute right-0 top-full z-[9999] mt-2 w-[min(16rem,calc(100vw-2rem))] translate-y-1 rounded-lg border px-3 py-2 text-xs opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100"
            >
              <span className="block leading-relaxed">
                {t('footer.linkedinTooltip')}
              </span>
            </span>
          </span>
          <div data-header-utility-actions className="hidden items-center gap-2 lg:flex">
            <span className="group relative inline-flex" data-header-linkedin>
              <a
                href={publicSocialLinks.linkedinProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="brand-nav-link brand-focus-ring inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors"
                aria-label={t('footer.linkedinAria')}
                aria-describedby="header-desktop-linkedin-tooltip"
              >
                <Linkedin className="h-5 w-5 brand-linkedin-icon" aria-hidden="true" />
              </a>
              <span
                id="header-desktop-linkedin-tooltip"
                role="tooltip"
                className="app-tooltip-surface pointer-events-none absolute right-0 top-full z-[9999] mt-2 w-[min(16rem,calc(100vw-2rem))] translate-y-1 rounded-lg border px-3 py-2 text-xs opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100"
              >
                <span className="block leading-relaxed">
                  {t('footer.linkedinTooltip')}
                </span>
              </span>
            </span>
            <span className="theme-header-separator h-6 w-px" data-header-utility-separator aria-hidden="true" />
            <LanguageSwitcher />
            <ThemeSwitcher />
            <span className="theme-header-separator h-6 w-px" data-header-utility-separator aria-hidden="true" />
            <button
              type="button"
              onClick={onSignIn}
              onFocus={onLoginIntent}
              onPointerEnter={onLoginIntent}
              data-header-login
              className="brand-button brand-button-link brand-focus-ring inline-flex h-10 w-auto items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold"
              aria-label={t('nav.login')}
              title={t('nav.login')}
            >
              <User className="h-4 w-4" />
              <span>{t('nav.login')}</span>
            </button>
          </div>
          <button
            type="button"
            onClick={onToggleMobileMenu}
            className="brand-focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-[rgb(var(--theme-text))] shadow-none transition-colors hover:text-[rgb(var(--theme-primary-strong))] min-[380px]:h-10 min-[380px]:w-10 lg:hidden"
            aria-label={t(isMobileMenuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
            aria-expanded={isMobileMenuOpen}
            aria-controls="public-mobile-navigation"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <div
        id="public-mobile-navigation"
        className={`min-h-0 overflow-y-auto overscroll-contain px-3 pb-3 lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
      >
        <div
          className="mx-3 mb-2 h-px bg-[rgb(var(--theme-border)/0.56)]"
          data-mobile-login-separator
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={onSignIn}
          onFocus={onLoginIntent}
          className="brand-button brand-button-link brand-focus-ring mb-2 flex min-h-11 w-full items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold"
        >
          <User className="h-4 w-4" />
          <span>{t('nav.login')}</span>
        </button>
        {navigationLinks.map((link) => (
          <a
            key={link.id}
            href={localizedHref(link.href ?? `/#${link.id}`)}
            onClick={onMobileNavigationClick
              ? (event) => {
                  if (!link.href) event.preventDefault();
                  onMobileNavigationClick(link.id);
                }
              : undefined}
            className={`brand-nav-link group relative flex min-h-11 w-full items-center rounded-md px-3 py-2.5 text-left text-sm font-semibold leading-tight transition-colors hover:bg-[rgb(var(--theme-primary)/0.08)] focus-visible:outline-none ${link.id === 'manifesto' || link.id === 'pricing' ? 'md:hidden' : ''}`}
          >
            <span>{t(link.labelKey)}</span>
          </a>
        ))}
        <div
          className="mt-2 flex items-center justify-center gap-2 border-t border-[rgb(var(--theme-border)/0.72)] pt-3"
          data-mobile-preferences
        >
          <LanguageSwitcher />
          <span className="theme-header-separator h-6 w-px" aria-hidden="true" />
          <ThemeSwitcher compact plain />
        </div>
      </div>
    </nav>
  );
}
